import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';

/**
 * "Insights by category" (SCRUM-328) — v6 `.dash-insight--static`: a
 * read-only list of the four intelligence categories with their basis
 * descriptions (verbatim from the prototype) and live flagged counts.
 * Deliberately NO drilldown — the v6 prototype marks this section
 * static ("no drilldown"), superseding the older matrix note.
 */
const CATEGORIES: { key: InsightType; label: string; desc: string; dotClass: string }[] = [
  {
    key: 'relgap',
    label: 'Relationship Gap',
    desc: 'Based on product holdings vs. tenure and balance — established or high-balance customers carrying only one or two products with no recent cross-sell.',
    dotClass: 'bg-insight-relgap',
  },
  {
    key: 'flight',
    label: 'Deposit Flight Risk',
    desc: 'Based on balance trend and withdrawal behavior — declining balances and large or repeated outflows that signal deposits may be leaving.',
    dotClass: 'bg-insight-flight',
  },
  {
    key: 'dormant',
    label: 'Dormant Account',
    desc: 'Based on transaction recency — accounts with no activity for an extended period (no posted transactions in the last several weeks).',
    dotClass: 'bg-insight-dormant',
  },
  {
    key: 'smb',
    label: 'SMB Growth',
    desc: 'Based on business deposit and transaction trends — small businesses with rising inflows and volume, scaling toward new products.',
    dotClass: 'bg-insight-smb',
  },
];

export function InsightsByCategoryCard({
  customers,
  branchLabel,
}: {
  customers: readonly Customer[];
  /** v7 (SCRUM-388): Branch Manager's card names the scoped branch at right. */
  branchLabel?: string | undefined;
}) {
  return (
    <section
      aria-label="Insights by category"
      className="bg-bg-4 border-border-0 rounded-tk-lg border p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="font-display text-text-0 text-base font-semibold">
          Insights by category
        </span>
        {branchLabel && <span className="text-text-3 text-xs">{branchLabel}</span>}
      </div>
      <div className="flex flex-col">
        {CATEGORIES.map((cat) => {
          const count = customers.filter((c) => c.insights[cat.key]?.active).length;
          return (
            <div
              key={cat.key}
              className="border-border-0 flex items-start gap-[11px] border-t px-1 py-3 first:border-t-0"
            >
              <span className={`mt-[5px] h-[9px] w-[9px] flex-none rounded-full ${cat.dotClass}`} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-text-0 text-sm font-medium">{cat.label}</span>
                <span className="text-text-3 text-[0.6875rem] leading-[1.45]">{cat.desc}</span>
              </div>
              <span className="font-display text-text-0 mt-px text-base font-semibold">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
